import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import "../SignupPage/signup-wizard.css";
import "./login-wizard.css";
import "../PasskeyPage/passkey.css";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { $ } from "react-jquery-plugin";
import { ProfileContext } from "../../../context/ProfileProvider";
import { useGoogleLogin } from '@react-oauth/google';
import { Helmet } from "react-helmet-async";
import { isValidPhoneNumber } from "libphonenumber-js";
import AuthPhoneField from "../../../customComponents/AuthPhoneField";
import EmailInputWithDomainSuggest from "../../../customComponents/EmailInputWithDomainSuggest";
import { startAuthentication } from "@simplewebauthn/browser";
import { getPasswordRuleMeta, validatePasswordAgainstRules } from "../../../utils/authPasswordRules";
import { getOrCreateDeviceId, persistDeviceIdFromServer } from "../../../utils/deviceId";
import LoginMethodSelectPanel from "./components/LoginMethodSelectPanel";
import LoginMethodCodePanel from "./components/LoginMethodCodePanel";
import LoginMethodRecoveryOverlays from "./components/LoginMethodRecoveryOverlays";
import {
  methodNeedsOtpSend,
  methodTypeToRecoveryKey,
  normalizeRecoverableMethodKeys,
  normalizeRecoveryMethodKey,
  recoveryKeyToMethodType,
  recoveryLostMethodLabel,
  recoveryMethodObject,
  recoveryRestrictionHours,
  isRecoveryVerifyComplete,
  recoveryVerifyErrorMessage,
} from "./components/loginVerifyHelpers";
import { parseIdentifierCheckResponse } from "../SignupPage/signupUtils";
import { isAppleSignInConfigured, signInWithApple } from "../../../utils/appleSignIn";
import ReCAPTCHA from "react-google-recaptcha";
import "./components/loginVerifyFlow.css";

const CAPTCHA_SITE_KEY = String(
  process.env.REACT_APP_CAPTCHA_SITE_KEY || "6LfetMotAAAAACd1W9IUPIQS7eDRPX8TzoxZSOWT"
)
  .trim()
  .replace(/^["']|["']$/g, "");

const AUTH_STORAGE_PREF_KEY = "agce.auth.rememberMe.v1";
function readRememberMeDefault() {
  try {
    const v = localStorage.getItem(AUTH_STORAGE_PREF_KEY);
    if (v == null) return true; // default: remember
    return v === "1" || v === "true";
  } catch {
    return true;
  }
}

function setRememberMeDefault(v) {
  try {
    localStorage.setItem(AUTH_STORAGE_PREF_KEY, v ? "1" : "0");
  } catch {
    // ignore
  }
}

function getStoredTokenAny() {
  try {
    return localStorage.getItem("token") || sessionStorage.getItem("token");
  } catch {
    return null;
  }
}

function clearAuthStorage() {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("userId");
  } catch {
    // ignore
  }
  try {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("userId");
  } catch {
    // ignore
  }
}

/**
 * Distinguish "no such user" vs "wrong password" from backend message (when possible).
 * Many APIs return a single "Invalid email or password" — treat as wrong-password (password field).
 */
function classifyLoginFailureMessage(message) {
  const m = String(message || "").toLowerCase();
  if (!m) return "auth_failed";
  if (m.includes("not been activated") || m.includes("not activated") || m.includes("verify your account")) {
    return "activation";
  }
  if (
    m.includes("user not found") ||
    m.includes("no user found") ||
    m.includes("does not exist") ||
    m.includes("not registered") ||
    m.includes("no account with") ||
    m.includes("no account found") ||
    m.includes("create an account") ||
    m.includes("email is not registered") ||
    m.includes("username is not found") ||
    m.includes("username not found")
  ) {
    return "user_not_found";
  }
  if (m.includes("password") && (m.includes("wrong") || m.includes("incorrect") || m.includes("invalid") || m.includes("mismatch"))) {
    return "wrong_password";
  }
  if (m.includes("invalid email or password") || m.includes("invalid credentials") || m.includes("wrong password")) {
    return "wrong_password";
  }
  return "auth_failed";
}

/**
 * `completePasskeyLogin` / discoverable verify often return token as `data.token`, `access_token`,
 * or `data` as a raw JWT string — strict `data.token` checks skipped login and sent users to password.
 */
function pickTokenUserIdFromLoginLikeResponse(res) {
  if (res == null || res.success === false) return null;
  const strip = (v) => (v == null ? "" : String(v).replace(/^Bearer\s+/i, "").trim());
  const d = res.data;
  if (typeof d === "string" && strip(d)) {
    return { token: strip(d), refreshToken: null, userId: res.userId ?? null, profile: res };
  }
  if (d && typeof d === "object") {
    const token =
      d.token ||
      d.accessToken ||
      d.access_token ||
      d.jwt ||
      d.sessionToken ||
      d.bearerToken ||
      (typeof d.data === "string" ? d.data : null) ||
      (d.data && typeof d.data === "object" ? d.data.token || d.data.accessToken || d.data.access_token : null);
    const refreshToken =
      d.refresh_token ||
      d.refreshToken ||
      (d.data && typeof d.data === "object" ? d.data.refresh_token || d.data.refreshToken : null) ||
      null;
    if (token && strip(token)) {
      return {
        token: strip(token),
        refreshToken: refreshToken ? strip(refreshToken) : null,
        userId: d.userId ?? d.id ?? d.user_id ?? null,
        profile: d,
        deviceId: d.device_id || null,
        sessionId: d.session_id || null,
      };
    }
  }
  const top = strip(res.token || res.accessToken || res.access_token);
  if (top) {
    return {
      token: top,
      refreshToken: strip(res.refresh_token || res.refreshToken) || null,
      userId: res.userId ?? null,
      profile: res,
    };
  }
  return null;
}

/** New challenge login (no tokens yet) — also accepts legacy requiresVerification. */
function isLoginChallengePending(data) {
  if (!data || typeof data !== "object") return false;
  if (data.access_token || data.token) return false;
  if (data.requiresVerification) return true;
  if (data.challenge_id || data.tempToken || data.temp_token || data.verification_mode) return true;
  if (Array.isArray(data.availableMethods) && data.availableMethods.length > 0) return true;
  return false;
}

const LOGIN_VERIFY_SESSION_KEY = "agce.login.verify.session.v1";
const RECOVERY_FORCE_LOGIN_KEY = "agce.login.recovery.forceLogin.v1";

function markForceLoginAfterRecovery() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage?.setItem(RECOVERY_FORCE_LOGIN_KEY, "1");
  } catch {
    // ignore
  }
}

function consumeForceLoginAfterRecovery() {
  if (typeof window === "undefined") return false;
  try {
    const v = window.sessionStorage?.getItem(RECOVERY_FORCE_LOGIN_KEY);
    window.sessionStorage?.removeItem(RECOVERY_FORCE_LOGIN_KEY);
    return v === "1";
  } catch {
    return false;
  }
}

function safeJsonParse(str) {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

function readVerifySession() {
  if (typeof window === "undefined") return null;
  const raw = window.sessionStorage?.getItem(LOGIN_VERIFY_SESSION_KEY);
  if (!raw) return null;
  return safeJsonParse(raw);
}

function writeVerifySession(data) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage?.setItem(LOGIN_VERIFY_SESSION_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

function clearVerifySession() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage?.removeItem(LOGIN_VERIFY_SESSION_KEY);
  } catch {
    // ignore
  }
}

/** Coerce API method type to 1|2|3|4 so SMS (3) is not missed when the server sends strings or aliases. */
function normalizeAuthMethodType(raw) {
  const n = Number(raw);
  if (n === 1 || n === 2 || n === 3 || n === 4) return n;
  const s = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (s === "email" || s === "mail") return 1;
  if (s === "google" || s === "authenticator" || s === "app" || s === "ga" || s === "totp") return 2;
  if (s === "mobile" || s === "phone" || s === "sms") return 3;
  if (s === "passkey" || s === "webauthn") return 4;
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function remainingMethodTypes(remaining) {
  if (!Array.isArray(remaining)) return [];
  return remaining
    .map((m) => {
      if (m == null) return null;
      if (typeof m === "number" || typeof m === "string") return normalizeAuthMethodType(m);
      return normalizeAuthMethodType(m.type ?? m.verificationType ?? m.methodType);
    })
    .filter((t) => t === 1 || t === 2 || t === 3 || t === 4);
}

const LoginPage = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const prevPathRef = useRef(location?.pathname || "");

  const [wizardStep, setWizardStep] = useState(1);
  const [loginPendingVerification, setLoginPendingVerification] = useState(false);
  /** Google opened 2FA without password step — dot 1 shows done on step 2 */
  const [verificationSkipsPasswordSteps, setVerificationSkipsPasswordSteps] = useState(false);
  const [accountTab, setAccountTab] = useState("email_user"); // email_user | phone | qr
  const [signId, setSignId] = useState("");
  const [password, setPassword] = useState("");
  const [loginCredentialStep, setLoginCredentialStep] = useState("identifier"); // identifier | password
  const [countryCode, setCountryCode] = useState("+91");
  const [showPassword, setShowPassword] = useState(false);
  const [bindIp, setBindIp] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => readRememberMeDefault());
  /** Empty / invalid email, username, or phone — show danger border on field */
  const [identifierDangerBorder, setIdentifierDangerBorder] = useState(false);
  /** API says login failed with likely wrong password (user exists / generic auth fail) */
  const [passwordDangerBorder, setPasswordDangerBorder] = useState(false);
  /** Step 1 primary "Next" — identifier / password submit */
  const [loginNextBusy, setLoginNextBusy] = useState(false);
  /** Step 2 OTP verification "Next" */
  const [loginOtpVerifyBusy, setLoginOtpVerifyBusy] = useState(false);

  // Enhanced 2FA Login states
  const [selectedAuthMethod, setSelectedAuthMethod] = useState(1); // 1=email, 2=google, 3=mobile, 4=passkey
  const [availableMethods, setAvailableMethods] = useState([]);
  /** Same as `availableMethods` — updated synchronously so `sendLoginOtp` sees methods in the same tick as login. */
  const availableMethodsRef = useRef([]);
  const [resendTimer, setResendTimer] = useState(0);
  const [loginSignId, setLoginSignId] = useState(""); // Store the signId used for login
  /** Same as `loginSignId` state — updated synchronously so `sendLoginOtp`/`getOtp` see the id in the same tick as `setLoginSignId`. */
  const loginSignIdRef = useRef("");
  /** When login started from the phone tab — OTP should use SMS + `+CC nnn` in `email_or_phone` */
  const [loginVerifyPhoneCountryCode, setLoginVerifyPhoneCountryCode] = useState("");
  /** Same as state, updated synchronously so `sendLoginOtp` works in the same tick as `handleLogin`. */
  const loginVerifyPhoneCountryCodeRef = useRef("");
  /** `"phone"` when password login used phone+digits — `/user/send-otp` must use SMS + `+CC digits`. */
  const loginCredentialKindRef = useRef("email_user");
  /** Login challenge (Redis) — required on verify-otp / passkey verify */
  const challengeIdRef = useRef("");
  const tempTokenRef = useRef("");
  const recaptchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaTheme, setCaptchaTheme] = useState(() =>
    typeof document !== "undefined" && document.body.classList.contains("light_theme") ? "light" : "dark"
  );
  const [verificationMode, setVerificationMode] = useState("");
  /** Method types still required on ALL_REQUIRED challenges */
  const [remainingMethods, setRemainingMethods] = useState([]);
  const [completedMethods, setCompletedMethods] = useState([]);
  /** Step 2 sub-flow: method list → code entry */
  const [verifySubStep, setVerifySubStep] = useState("methods"); // methods | code
  const [verifyMethodBusyType, setVerifyMethodBusyType] = useState(null);
  /** Lost Login 2-Step method recovery (Can't access authenticator / email / phone) */
  const [recoverableMethods, setRecoverableMethods] = useState([]);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [recoveryLostMethod, setRecoveryLostMethod] = useState("");
  const [recoveryRemainingKeys, setRecoveryRemainingKeys] = useState([]);
  const [recoveryVerifiedKeys, setRecoveryVerifiedKeys] = useState([]);
  const [recoveryStartBusy, setRecoveryStartBusy] = useState(false);
  /** Overlay flow: select → warn → verify remaining method → success */
  const [recoveryUiStep, setRecoveryUiStep] = useState(null); // null | select | warn | verify | success
  const [recoverySelectLost, setRecoverySelectLost] = useState("");
  const [recoveryResetAck, setRecoveryResetAck] = useState(false);
  const [recoveryRestrictionHrs, setRecoveryRestrictionHrs] = useState(24);
  const recoveryRedirectTimerRef = useRef(null);
  const isRecoveryModeRef = useRef(false);
  const recoveryUiStepRef = useRef(null);
  const [otpSingle, setOtpSingle] = useState("");
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  /** Hide passkey overlay before opening "Choose verification method" so two modals never stack */
  const [suppressPasskeyOverlay, setSuppressPasskeyOverlay] = useState(false);
  const otpInputRefs = useRef([]);
  /** Last OTP string we auto-submitted (login step 2) — avoids double verify / loops after failure. */
  const loginAutoVerifyLastOtpRef = useRef("");
  const loginAutoVerifyInFlightRef = useRef(false);
  const handleAuthVerifyRef = useRef(async () => {});
  /** Set when user dismisses the WebAuthn dialog — skip further auto passkey prompts; use email/SMS/etc. */
  const passkeyCancelledThisLoginRef = useRef(false);

  const navigate = useNavigate();
  recoveryUiStepRef.current = recoveryUiStep;

  const { setLoginDetails } = useContext(ProfileContext);

  const persistSessionAndRedirect = (loginApiResult, opts) => {
    const { successMessage, remember } = opts || {};
    const session = pickTokenUserIdFromLoginLikeResponse(loginApiResult);
    if (!session?.token) return false;

    // Persist preference (used only for storage choice).
    setRememberMeDefault(Boolean(remember));

    // Avoid stale tokens across storage types.
    clearAuthStorage();

    const storage = remember ? window.localStorage : window.sessionStorage;
    try {
      getOrCreateDeviceId();
      if (session.deviceId) persistDeviceIdFromServer(session.deviceId);
      storage.setItem("token", session.token);
      if (session.refreshToken) storage.setItem("refresh_token", session.refreshToken);
      if (session.userId != null && session.userId !== "") {
        storage.setItem("userId", String(session.userId));
      }
    } catch {
      // If storage fails (private mode / quota), fallback to localStorage best-effort.
      try {
        localStorage.setItem("token", session.token);
        if (session.refreshToken) localStorage.setItem("refresh_token", session.refreshToken);
        if (session.userId != null && session.userId !== "") {
          localStorage.setItem("userId", String(session.userId));
        }
      } catch {
        // ignore
      }
    }

    // if (successMessage) alertSuccessMessage(successMessage);

    const profile =
      session.profile && typeof session.profile === "object" && !Array.isArray(session.profile)
        ? session.profile
        : { token: session.token, userId: session.userId };
    setLoginDetails(profile);
    window.dispatchEvent(new Event("agce-auth-changed"));
    window.dispatchEvent(new Event("agce-auth-token-changed"));

    clearVerifySession();
    challengeIdRef.current = "";
    tempTokenRef.current = "";
    setVerificationMode("");
    setRemainingMethods([]);
    setCompletedMethods([]);
    setVerifySubStep("methods");
    setVerifyMethodBusyType(null);
    setRecoverableMethods([]);
    setIsRecoveryMode(false);
    isRecoveryModeRef.current = false;
    setRecoveryLostMethod("");
    setRecoveryRemainingKeys([]);
    setRecoveryVerifiedKeys([]);
    setRecoveryUiStep(null);
    setRecoverySelectLost("");
    setRecoveryResetAck(false);
    setRecoveryRestrictionHrs(24);
    // Opens the dashboard with the KYC modal; verified users just get the dashboard.
    const redirectPath = location?.state?.redirectTo || "/kyc-verification";
    const go = () => navigate(redirectPath, { replace: true });
    if (successMessage) {
      window.setTimeout(go, 100);
    } else {
      go();
    }
    return true;
  };

  const persistPasskeyLoginAndRedirect = (loginApiResult) =>
    persistSessionAndRedirect(loginApiResult, { successMessage: "Login success!", remember: rememberMe });

  // If user is already logged in, redirect away from login.
  useEffect(() => {
    // Only run on login routes (avoid side-effects on other pages importing this component).
    const path = location?.pathname || "";
    if (path !== "/login" && path !== "/auth/verify") return;
    const existing = getStoredTokenAny();
    if (!existing) return;
    const redirectPath = location?.state?.redirectTo || "/user_profile/dashboard";
    navigate(redirectPath, { replace: true });
  }, [location?.pathname, location?.state?.redirectTo, navigate]);

  const loginUsernamePart = useMemo(() => {
    if (accountTab === "phone") {
      return String(signId || "").replace(/\D/g, "");
    }
    const s = String(signId || "");
    if (s.includes("@")) {
      return s.split("@")[0] || "";
    }
    return s.trim();
  }, [accountTab, signId]);

  const loginPasswordRules = useMemo(() => getPasswordRuleMeta(loginUsernamePart), [loginUsernamePart]);

  // Add loginbg class on mount
  useEffect(() => {
    $("body").addClass("loginbg");

    // `public/index.html` toggles loginbg only for "/login".
    // We must also keep it for "/auth/verify" without changing HTML.
    const keepClassId = window.setInterval(() => {
      try {
        if (window.location.pathname === "/login" || window.location.pathname === "/auth/verify") {
          $("body").addClass("loginbg");
        }
      } catch {
        // ignore
      }
    }, 250);

    // Check if WebAuthn/Passkey is supported AND platform authenticator is available
    const checkPasskeySupport = async () => {
      // First check if WebAuthn API exists
      if (window.PublicKeyCredential === undefined || typeof window.PublicKeyCredential !== 'function') {
        setPasskeySupported(false);
        return;
      }

      // Detect iOS/iPadOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

      // Detect Safari on iOS
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

      // Check if platform authenticator (fingerprint/Face ID/Windows Hello) is available
      try {
        const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();

        // On iOS Safari 16+, passkeys are supported even if this returns false sometimes
        // This can happen if iCloud Keychain is temporarily unavailable
        if (!available && isIOS && isSafari) {
          // Check iOS version - passkeys require iOS 16+
          const match = navigator.userAgent.match(/OS (\d+)_/);
          const iosVersion = match ? parseInt(match[1], 10) : 0;

          if (iosVersion >= 16) {
            // iOS 16+ Safari should support passkeys, enable the option
            setPasskeySupported(true);
            return;
          }
        }

        setPasskeySupported(available);
      } catch (error) {
        // If check fails on iOS Safari, still allow trying passkeys
        if (isIOS && isSafari) {
          setPasskeySupported(true);
          return;
        }
        setPasskeySupported(false);
      }
    };

    checkPasskeySupport();

    return () => {
      window.clearInterval(keepClassId);
      $("body").removeClass("loginbg");
    };
  }, []);

  useEffect(() => {
    const syncCaptchaTheme = () => {
      const next = document.body.classList.contains("light_theme") ? "light" : "dark";
      setCaptchaTheme((current) => {
        if (current !== next) setCaptchaToken(null);
        return next;
      });
    };
    syncCaptchaTheme();
    const observer = new MutationObserver(syncCaptchaTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [wizardStep, accountTab]);

  useEffect(() => {
    if (wizardStep !== 1) return;
    // Reset to identifier-only step when user lands on login step 1
    setLoginCredentialStep("identifier");
    setPassword("");
    setShowPassword(false);
    setPasswordDangerBorder(false);
  }, [wizardStep, accountTab]);

  useEffect(() => {
    if (wizardStep !== 2 || verifySubStep !== "code" || selectedAuthMethod === 4) return undefined;
    const id = window.setTimeout(() => otpInputRefs.current[0]?.focus(), 120);
    return () => window.clearTimeout(id);
  }, [wizardStep, selectedAuthMethod, verifySubStep]);

  useEffect(() => {
    if (wizardStep === 2 && !loginPendingVerification) {
      setWizardStep(1);
    }
  }, [wizardStep, loginPendingVerification]);

  // URL ↔ wizard step syncing (AGCX-style):
  // - Step 1 uses `/login`
  // - Step 2 uses `/auth/verify?type=1&action=login`
  useEffect(() => {
    const path = location?.pathname || "";

    const prevPath = prevPathRef.current;
    prevPathRef.current = path;

    if (path === "/auth/verify") {
      if (consumeForceLoginAfterRecovery()) {
        clearVerifySession();
        challengeIdRef.current = "";
        tempTokenRef.current = "";
        navigate("/login", { replace: true });
        return;
      }
      const ss = readVerifySession();
      if (!ss?.pending) {
        navigate("/login", { replace: true });
        return;
      }

      setLoginPendingVerification(true);
      setWizardStep(2);
      setVerificationSkipsPasswordSteps(Boolean(ss.verificationSkipsPasswordSteps));
      const restoredSid = String(ss.loginSignId || "").trim();
      loginSignIdRef.current = restoredSid;
      setLoginSignId(restoredSid);
      const restoredCc = String(ss.loginOtpPhoneCountryCode || "").trim();
      loginVerifyPhoneCountryCodeRef.current = restoredCc;
      setLoginVerifyPhoneCountryCode(restoredCc);
      const restoredKind = ss.loginCredentialKind === "phone" ? "phone" : "email_user";
      loginCredentialKindRef.current = restoredKind;
      setSelectedAuthMethod(ss.selectedAuthMethod || 1);
      const restoredMethods = Array.isArray(ss.availableMethods) ? ss.availableMethods : [];
      availableMethodsRef.current = restoredMethods;
      setAvailableMethods(restoredMethods);
      challengeIdRef.current = String(ss.challenge_id || "").trim();
      tempTokenRef.current = String(ss.tempToken || "").trim();
      setVerificationMode(String(ss.verification_mode || ""));
      setRemainingMethods(remainingMethodTypes(ss.remaining_methods));
      setCompletedMethods(remainingMethodTypes(ss.completed_methods));
      setRecoverableMethods(normalizeRecoverableMethodKeys(ss.recoverable_methods));
      setIsRecoveryMode(Boolean(ss.isRecoveryMode));
      setRecoveryLostMethod(normalizeRecoveryMethodKey(ss.recoveryLostMethod));
      setRecoveryRemainingKeys(normalizeRecoverableMethodKeys(ss.recoveryRemainingKeys));
      setRecoveryVerifiedKeys(normalizeRecoverableMethodKeys(ss.recoveryVerifiedKeys));
      setRecoverySelectLost(normalizeRecoveryMethodKey(ss.recoverySelectLost || ss.recoveryLostMethod));
      setRecoveryResetAck(Boolean(ss.recoveryResetAck));
      setRecoveryRestrictionHrs(recoveryRestrictionHours(ss.recoveryRestrictionHrs));
      const restoredRecoveryStep = ["select", "warn", "requirements", "verify", "success"].includes(ss.recoveryUiStep)
        ? ss.recoveryUiStep
        : ss.isRecoveryMode
          ? "requirements"
          : null;
      setRecoveryUiStep(restoredRecoveryStep);
      isRecoveryModeRef.current = Boolean(ss.isRecoveryMode || restoredRecoveryStep === "verify" || restoredRecoveryStep === "requirements" || restoredRecoveryStep === "success");
      setVerifySubStep(ss.isRecoveryMode || restoredRecoveryStep ? "methods" : ss.verifySubStep === "code" ? "code" : "methods");
      if (ss.device_id) persistDeviceIdFromServer(ss.device_id);
      setResendTimer(Number(ss.resendTimer || 0));
      setOtpSingle("");
      return;
    }

    if (path === "/login") {
      consumeForceLoginAfterRecovery();
      // If user navigates back to /login manually, reset step-2 state.
      setWizardStep((s) => (s === 1 ? s : 1));
      setLoginPendingVerification(false);
      setVerificationSkipsPasswordSteps(false);
      setOtpSingle("");
      setResendTimer(0);
      availableMethodsRef.current = [];
      setAvailableMethods([]);
      loginSignIdRef.current = "";
      setLoginSignId("");
      loginVerifyPhoneCountryCodeRef.current = "";
      setLoginVerifyPhoneCountryCode("");
      loginCredentialKindRef.current = "email_user";
      setVerifySubStep("methods");
      setVerifyMethodBusyType(null);
      setRecoverableMethods([]);
      setIsRecoveryMode(false);
      setRecoveryLostMethod("");
      setRecoveryRemainingKeys([]);
      setRecoveryVerifiedKeys([]);
      setRecoveryUiStep(null);
      setRecoverySelectLost("");
      setRecoveryResetAck(false);
      setRecoveryRestrictionHrs(24);
      isRecoveryModeRef.current = false;
      clearVerifySession();

      // If the user reached /login via browser Back from /auth/verify,
      // clear the Forward stack so Forward does not return to verification.
      if (navigationType === "POP" && prevPath === "/auth/verify") {
        navigate("/login");
      }
    }
  }, [location.pathname, navigationType, navigate]);

  useEffect(() => {
    const $modal = $("#VerificationOptionsModal");
    if (!$modal?.length) return undefined;
    const onHidden = () => setSuppressPasskeyOverlay(false);
    $modal.on("hidden.bs.modal", onHidden);
    return () => {
      $modal.off("hidden.bs.modal", onHidden);
    };
  }, []);

  useEffect(() => {
    if (wizardStep !== 2) setSuppressPasskeyOverlay(false);
  }, [wizardStep]);

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  // Determine available verification methods based on backend response
  // Priority: Passkey (4) > Google Auth (2) > Email (1) > Mobile (3)
  const determineAvailableMethods = (responseData) => {
    let methods = [];

    if (responseData?.availableMethods && responseData.availableMethods.length > 0) {
      methods = responseData.availableMethods.map((m) => {
        const type = normalizeAuthMethodType(m.type ?? m.verificationType ?? m.methodType);
        return {
          ...m,
          type,
          label:
            m.label ||
            (type === 1
              ? "Email"
              : type === 2
                ? "Authenticator App"
                : type === 3
                  ? "Phone Number"
                  : "Passkeys"),
          icon:
            type === 1
              ? "ri-mail-line"
              : type === 2
                ? "ri-shield-keyhole-line"
                : type === 3
                  ? "ri-smartphone-line"
                  : "ri-fingerprint-line",
          description:
            type === 1
              ? "Receive verification codes via email"
              : type === 2
                ? "Use Google Authenticator app"
                : type === 3
                  ? "Receive verification codes via SMS"
                  : "Use Face ID, Touch ID, or Windows Hello",
        };
      });
    }

    // Check if user has passkey enabled AND browser supports it.
    // Never show passkey UI unless backend explicitly says user has passkey registered/enabled.
    const hasPasskeyFlag =
      !!responseData?.hasPasskey || methods.some((m) => m.type === 4);
    if (hasPasskeyFlag && passkeySupported) {
      const passkeyMethod = {
        type: 4,
        name: "Passkeys",
        label: "Passkeys",
        icon: "ri-fingerprint-line",
        description: "Use Face ID, Touch ID, or Windows Hello",
        maskedValue: "Face ID, Touch ID, or Windows Hello",
      };
      methods = [passkeyMethod, ...methods.filter((m) => m.type !== 4)];
    }

    // Stable UI order: Email → Phone → Authenticator → Passkeys
    const orderRank = (t) => {
      if (t === 1) return 1;
      if (t === 3) return 2;
      if (t === 2) return 3;
      if (t === 4) return 4;
      return 99;
    };
    methods = [...methods].sort((a, b) => orderRank(a.type) - orderRank(b.type));

    // If backend sends no methods (or API failed upstream), fallback to Email OTP so user never gets stuck.
    if (!methods || methods.length === 0) {
      methods = [
        {
          type: 1,
          name: "Email",
          label: "Email",
          icon: "ri-mail-line",
          description: "Receive verification codes via email",
        },
      ];
    }

    return methods;
  };

  /** After user cancels passkey once, prefer OTP/authenticator; still allow passkey if user picks it in the picker. */
  const choose2FADefaultMethod = (responseData, methods, userDeclinedPasskey, preferSmsForPhoneCredential) => {
    const has = (t) => methods.some((m) => m.type === t);
    const dm = responseData?.defaultMethod;

    /** Server often defaults to email (1) for phone logins; still use SMS when user signed in on the phone tab. */
    const preferSmsOverEmailDefault = () =>
      preferSmsForPhoneCredential && has(3) && dm === 1 ? 3 : null;

    if (userDeclinedPasskey) {
      const smsOverEmail = preferSmsOverEmailDefault();
      if (smsOverEmail != null) return smsOverEmail;
      if (dm != null && dm !== "" && dm !== 4 && has(dm)) {
        return dm;
      }
      if (preferSmsForPhoneCredential && has(3)) return 3;
      if (has(1)) return 1;
      if (has(3)) return 3;
      if (has(2)) return 2;
      if (has(4)) return 4;
      return 1;
    }

    const smsOverEmail = preferSmsOverEmailDefault();
    if (smsOverEmail != null) return smsOverEmail;

    // By default, do NOT force passkey just because it's available.
    // Only pick passkey automatically when backend explicitly asks for it (defaultMethod === 4),
    // otherwise prefer OTP/authenticator options.
    if (dm != null && dm !== "" && has(dm)) {
      return dm;
    }
    if (preferSmsForPhoneCredential && has(3)) return 3;
    if (has(1)) return 1; // email
    if (has(3)) return 3; // sms
    if (has(2)) return 2; // authenticator
    if (has(4)) return 4; // passkey fallback
    return 1;
  };

  const digitsOnlySignId = (value) =>
    String(value ?? "")
      .replace(/\D/g, "")
      .replace(/^0+/, "") || "";

  /** Backend expects a single `email_or_phone` string, e.g. `+91 6376747457` (no extra keys). */
  const formatPhoneForUserOtpPayload = (rawValue, fallbackDigits) => {
    const trimmed = String(rawValue ?? "").trim();
    if (trimmed.startsWith("+")) return trimmed;
    const digits = digitsOnlySignId(trimmed) || digitsOnlySignId(fallbackDigits);
    const ccRaw = String(loginVerifyPhoneCountryCodeRef.current || loginVerifyPhoneCountryCode || "").trim();
    const cc =
      ccRaw ||
      (loginCredentialKindRef.current === "phone" ? String(countryCode || "+91").trim() : "");
    if (!cc || !digits) return trimmed || fallbackDigits || "";
    return `${cc} ${digits}`.trim();
  };

  // Send OTP for login verification (resend)
  const sendLoginOtp = async (method) => {
    try {
      // Google Auth and Passkey don't need OTP sending
      if (method === 2 || method === 4) {
        return true;
      }

      LoaderHelper.loaderStatus(true);

      const isPhoneCredential = loginCredentialKindRef.current === "phone";
      // Phone password login: always hit `/user/send-otp` as SMS (`sendTo: mobile`, `+CC digits`).
      const otpChannelMethod = isPhoneCredential ? 3 : method;
      const sendTo = otpChannelMethod === 3 ? "mobile" : "email";

      const methodsForOtp =
        availableMethodsRef.current && availableMethodsRef.current.length
          ? availableMethodsRef.current
          : availableMethods;

      // `email_or_phone`: email string, or single international phone string (+CC digits) — no extra keys.
      const effectiveLoginId = String(loginSignIdRef.current || loginSignId || "").trim();
      let otpSignId = effectiveLoginId;
      if (otpChannelMethod === 3) {
        const mobileMethod = methodsForOtp.find((m) => m.type === 3);
        otpSignId = formatPhoneForUserOtpPayload(
          mobileMethod?.value != null ? mobileMethod.value : effectiveLoginId,
          effectiveLoginId
        );
      } else if (otpChannelMethod === 1) {
        const emailMethod = methodsForOtp.find((m) => m.type === 1);
        if (emailMethod?.value) {
          otpSignId = emailMethod.value;
        }
      }

      if (!String(otpSignId || "").trim()) {
        alertErrorMessage("Missing login identifier. Go back and sign in again.");
        return false;
      }

      const result = await AuthService.getOtp(otpSignId, 'login', sendTo);
      if (result?.success) {
        // alertSuccessMessage(result?.message || 'OTP sent successfully');
        setResendTimer(60);
        return true;
      } else {
        alertErrorMessage(result?.message || 'Failed to send OTP');
        return false;
      }
    } catch (error) {
      alertErrorMessage(error?.message || 'Failed to send OTP');
      return false;
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const startEmailOtpFallback = async (opts) => {
    const { signIdForSession, methodsForSession, verificationSkipsPasswordSteps } = opts || {};
    const sid = String(signIdForSession || loginSignIdRef.current || loginSignId || "").trim();
    loginSignIdRef.current = sid;
    setLoginSignId(sid);
    const am = Array.isArray(methodsForSession) && methodsForSession.length ? methodsForSession : availableMethods;
    availableMethodsRef.current = am;
    setAvailableMethods(am);
    loginVerifyPhoneCountryCodeRef.current = "";
    setLoginVerifyPhoneCountryCode("");
    loginCredentialKindRef.current = "email_user";
    setSelectedAuthMethod(1);
    setOtpSingle("");
    setVerifySubStep("methods");
    setVerifyMethodBusyType(null);
    setVerificationSkipsPasswordSteps(Boolean(verificationSkipsPasswordSteps));
    setLoginPendingVerification(true);
    setWizardStep(2);
    writeVerifySession({
      pending: true,
      verifySubStep: "methods",
      loginSignId: sid,
      loginOtpPhoneCountryCode: "",
      loginCredentialKind: "email_user",
      availableMethods: Array.isArray(methodsForSession) && methodsForSession.length ? methodsForSession : availableMethods,
      selectedAuthMethod: 1,
      resendTimer: 0,
      verificationSkipsPasswordSteps: Boolean(verificationSkipsPasswordSteps),
      challenge_id: challengeIdRef.current,
      tempToken: tempTokenRef.current,
      verification_mode: verificationMode,
    });
    navigate("/auth/verify?type=1&action=login", { replace: true });
  };

  // Handle passkey authentication
  const handlePasskeyAuth = async () => {
    if (!passkeySupported) {
      alertErrorMessage('Passkeys are not supported on this device/browser');
      return;
    }

    try {
      setIsPasskeyLoading(true);
      LoaderHelper.loaderStatus(true);

      // Step 1: Get authentication options from server
      const optionsResult = await AuthService.passkeyGetAuthOptions(loginSignId);
      if (!optionsResult?.success || !optionsResult?.data) {
        alertErrorMessage(optionsResult?.message || 'Failed to get passkey options');
        return;
      }

      const authOptions = optionsResult.data;

      // Step 2: Authenticate using browser's WebAuthn API
      let credential;
      try {
        credential = await startAuthentication(authOptions);
      } catch (webauthnError) {
        if (webauthnError.name === 'NotAllowedError') {
          alertErrorMessage('Authentication was cancelled or timed out. Please try again.');
        } else {
          alertErrorMessage('Failed to authenticate with passkey. Please try another method.');
        }
        return;
      }

      // Step 3: Verify credential with server
      const verifyResult = await AuthService.passkeyVerifyAuth(loginSignId, credential, challengeOptsFromRefs());

      if (verifyResult?.success) {
        // Passkey-only: tokens from verify → logged in
        if (persistPasskeyLoginAndRedirect(verifyResult)) {
          return;
        }
        if (verifyResult?.data?.verification_complete === false) {
          handleIntermediateVerifyProgress(verifyResult.data);
          return;
        }
        const loginResult = await AuthService.completePasskeyLogin(
          loginSignId,
          verifyResult.data,
          challengeOptsFromRefs()
        );
        if (persistPasskeyLoginAndRedirect(loginResult)) {
          return;
        }
        if (loginResult?.data?.verification_complete === false) {
          handleIntermediateVerifyProgress(loginResult.data);
          return;
        }
        alertErrorMessage(loginResult?.message || "Login failed");
      } else {
        alertErrorMessage(verifyResult?.message || 'Passkey verification failed');
      }
    } catch (error) {
      alertErrorMessage(error?.message || 'Something went wrong');
    } finally {
      setIsPasskeyLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const attemptSilentPasskeyLoginVerification = async (signIdForPasskey) => {
    if (!passkeySupported) {
      return { success: false, reason: "not_supported" };
    }

    try {
      const optionsResult = await AuthService.passkeyGetAuthOptions(signIdForPasskey);
      if (!optionsResult?.success || !optionsResult?.data) {
        return { success: false, reason: "no_options" };
      }

      const authOptions = optionsResult.data;

      let credential;
      try {
        credential = await startAuthentication(authOptions);
      } catch {
        return { success: false, reason: "cancelled" };
      }

      const verifyResult = await AuthService.passkeyVerifyAuth(signIdForPasskey, credential, challengeOptsFromRefs());

      if (verifyResult?.success) {
        return { success: true, verifyData: verifyResult.data };
      }
      return { success: false, reason: "verification_failed" };
    } catch {
      return { success: false, reason: "error" };
    }
  };

  const challengeOptsFromRefs = () => ({
    challenge_id: challengeIdRef.current || undefined,
    tempToken: tempTokenRef.current || undefined,
  });

  const applyChallengeFromLoginData = (responseData) => {
    if (responseData?.challenge_id) challengeIdRef.current = String(responseData.challenge_id);
    if (responseData?.tempToken) tempTokenRef.current = String(responseData.tempToken);
    else if (responseData?.temp_token) tempTokenRef.current = String(responseData.temp_token);
    if (responseData?.verification_mode) setVerificationMode(String(responseData.verification_mode));
    if (responseData?.device_id) persistDeviceIdFromServer(responseData.device_id);
    else getOrCreateDeviceId();
  };

  const captureRecoveryHints = (responseData) => {
    const keys = normalizeRecoverableMethodKeys(responseData?.recoverable_methods);
    setRecoverableMethods(keys);
    setIsRecoveryMode(false);
    isRecoveryModeRef.current = false;
    setRecoveryLostMethod("");
    setRecoveryRemainingKeys([]);
    setRecoveryVerifiedKeys([]);
    setRecoveryUiStep(null);
    setRecoverySelectLost("");
    setRecoveryResetAck(false);
    return keys;
  };

  const handleIntermediateVerifyProgress = (data) => {
    const remaining = remainingMethodTypes(data?.remaining_methods);
    const completed = Array.isArray(data?.completed_methods)
      ? remainingMethodTypes(data.completed_methods)
      : [];
    if (data?.challenge_id) challengeIdRef.current = String(data.challenge_id);
    if (data?.verification_mode) setVerificationMode(String(data.verification_mode));
    setRemainingMethods(remaining);
    setCompletedMethods(completed);
    setVerifySubStep("methods");
    setVerifyMethodBusyType(null);
    setOtpSingle("");
    setSuppressPasskeyOverlay(false);

    alertSuccessMessage(
      data?.message ||
        (remaining.length
          ? `Method verified. ${remaining.length} method(s) remaining.`
          : "Method verified. Continue with remaining security methods.")
    );

    const ss = readVerifySession() || {};
    writeVerifySession({
      ...ss,
      pending: true,
      verifySubStep: "methods",
      challenge_id: challengeIdRef.current,
      tempToken: tempTokenRef.current,
      verification_mode: verificationMode || data?.verification_mode || ss.verification_mode,
      completed_methods: completed,
      remaining_methods: remaining,
    });
  };

  const loginOtpIdentifier = () => {
    const isPhoneCredential = loginCredentialKindRef.current === "phone";
    const effectiveLoginId = String(loginSignIdRef.current || loginSignId || "").trim();
    if (isPhoneCredential) {
      return formatPhoneForUserOtpPayload(effectiveLoginId, effectiveLoginId);
    }
    return effectiveLoginId;
  };

  const sendRecoveryOtp = async (methodKey) => {
    const key = normalizeRecoveryMethodKey(methodKey);
    if (key !== "email" && key !== "phone") return true;
    const sendTo = key === "phone" ? "mobile" : "email";
    const methodsForOtp =
      availableMethodsRef.current && availableMethodsRef.current.length
        ? availableMethodsRef.current
        : availableMethods;
    const effectiveLoginId = String(loginSignIdRef.current || loginSignId || "").trim();
    let otpSignId = effectiveLoginId;
    if (key === "phone") {
      const mobileMethod = methodsForOtp.find((m) => Number(m.type) === 3);
      otpSignId = formatPhoneForUserOtpPayload(
        mobileMethod?.value != null ? mobileMethod.value : effectiveLoginId,
        effectiveLoginId
      );
    } else {
      const emailMethod = methodsForOtp.find((m) => Number(m.type) === 1);
      if (emailMethod?.value) otpSignId = emailMethod.value;
    }
    if (!String(otpSignId || "").trim()) {
      alertErrorMessage("Missing login identifier. Go back and sign in again.");
      return false;
    }
    try {
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.getOtp(otpSignId, "login", sendTo);
      if (result?.success) {
        setResendTimer(60);
        return true;
      }
      alertErrorMessage(result?.message || "Failed to send OTP");
      return false;
    } catch (error) {
      alertErrorMessage(error?.message || "Failed to send OTP");
      return false;
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const persistRecoverySession = (extra = {}) => {
    const ss = readVerifySession() || {};
    writeVerifySession({
      ...ss,
      pending: true,
      challenge_id: challengeIdRef.current,
      tempToken: tempTokenRef.current,
      ...extra,
    });
  };

  const applyRecoveryProgress = async (data, opts = {}) => {
    const remaining = normalizeRecoverableMethodKeys(data?.remaining_methods);
    const verified = normalizeRecoverableMethodKeys(data?.verified_methods);
    const lost = normalizeRecoveryMethodKey(data?.lost_method) || recoveryLostMethod;
    const next = normalizeRecoveryMethodKey(data?.next_method) || remaining[0];
    const nextType = recoveryKeyToMethodType(next);
    const fromStart = opts.fromStart === true;

    isRecoveryModeRef.current = true;
    setIsRecoveryMode(true);
    setRecoveryLostMethod(lost);
    setRecoveryRemainingKeys(remaining);
    setRecoveryVerifiedKeys(verified);
    setOtpSingle("");
    setSuppressPasskeyOverlay(false);
    setVerifySubStep("methods");

    if (nextType) {
      setSelectedAuthMethod(nextType);
    }

    // After OTP: show remaining-methods list if more proofs are needed.
    // After start with a single remaining method: open that OTP popup directly.
    const nextStep =
      opts.forceStep ||
      (!fromStart && remaining.length > 0 ? "requirements" : nextType ? "verify" : "requirements");
    setRecoveryUiStep(nextStep);

    persistRecoverySession({
      isRecoveryMode: true,
      recoveryLostMethod: lost,
      recoveryRemainingKeys: remaining,
      recoveryVerifiedKeys: verified,
      recoverySelectLost: lost,
      recoveryUiStep: nextStep,
      verifySubStep: "methods",
      selectedAuthMethod: nextType || selectedAuthMethod,
    });
    if (nextStep === "verify" && (next === "email" || next === "phone")) {
      await sendRecoveryOtp(next);
    }
  };

  const handleStartMethodRecovery = async (lostMethod) => {
    const lost = normalizeRecoveryMethodKey(lostMethod);
    if (!lost || recoveryStartBusy) return;
    if (!tempTokenRef.current) {
      alertErrorMessage("Login challenge expired. Please sign in again.");
      clearVerifySession();
      navigate("/login", { replace: true });
      return;
    }

    setRecoveryStartBusy(true);
    try {
      const result = await AuthService.loginMethodRecoveryStart({
        tempToken: tempTokenRef.current,
        challenge_id: challengeIdRef.current,
        lost_method: lost,
      });

      if (
        result?.code === "DEVICE_MISMATCH_CHALLENGE" ||
        result?.code === "CHALLENGE_INVALID" ||
        result?.code === "CHALLENGE_EXPIRED"
      ) {
        alertErrorMessage(result?.message || "Login challenge expired. Please sign in again.");
        clearVerifySession();
        navigate("/login", { replace: true });
        return;
      }

      if (!result?.success) {
        alertErrorMessage(result?.message || "Could not start method recovery");
        return;
      }

      await applyRecoveryProgress(result.data || {}, { fromStart: true });
    } catch (error) {
      alertErrorMessage(error?.message || "Could not start method recovery");
    } finally {
      setRecoveryStartBusy(false);
    }
  };

  const goToLoginAfterRecovery = () => {
    markForceLoginAfterRecovery();
    clearAuthStorage();
    clearVerifySession();
    challengeIdRef.current = "";
    tempTokenRef.current = "";
    isRecoveryModeRef.current = false;
    setRecoveryUiStep("success");
    setOtpSingle("");
    window.location.replace("/login");
  };

  const handleRecoveryVerify = async (otpCode) => {
    if (recoveryRedirectTimerRef.current || recoveryUiStep === "success") return;
    const method =
      methodTypeToRecoveryKey(selectedAuthMethod) || recoveryRemainingKeys[0] || "";
    if (!method) {
      alertErrorMessage("Select a remaining method to verify.");
      return;
    }

    const result = await AuthService.loginMethodRecoveryVerify({
      tempToken: tempTokenRef.current,
      challenge_id: challengeIdRef.current,
      method,
      code: otpCode,
    });

    if (
      result?.code === "DEVICE_MISMATCH_CHALLENGE" ||
      result?.code === "CHALLENGE_INVALID" ||
      result?.code === "CHALLENGE_EXPIRED"
    ) {
      alertErrorMessage(result?.message || "Login challenge expired. Please sign in again.");
      clearVerifySession();
      navigate("/login", { replace: true });
      return;
    }

    if (!result?.success) {
      setOtpSingle("");
      alertErrorMessage(recoveryVerifyErrorMessage(result));
      return;
    }

    if (!isRecoveryVerifyComplete(result)) {
      const remaining = normalizeRecoverableMethodKeys(result?.data?.remaining_methods);
      if (remaining.length > 0) {
        await applyRecoveryProgress(result.data || {}, { forceStep: "verify" });
        return;
      }
    }

    goToLoginAfterRecovery();
  };

  const resetRecoveryUi = () => {
    isRecoveryModeRef.current = false;
    setRecoveryUiStep(null);
    setRecoveryResetAck(false);
    setIsRecoveryMode(false);
    setRecoveryLostMethod("");
    setRecoveryRemainingKeys([]);
    setRecoveryVerifiedKeys([]);
    setOtpSingle("");
  };

  const openRecoveryChooser = () => {
    const first = recoverableMethods[0] || "";
    const nextLost = recoverySelectLost || (recoverableMethods.length === 1 ? first : "");
    setRecoverySelectLost(nextLost);
    setRecoveryResetAck(false);
    setOtpSingle("");
    setVerifySubStep("methods");
    setRecoveryUiStep("select");
    persistRecoverySession({
      recoveryUiStep: "select",
      recoverySelectLost: nextLost,
      verifySubStep: "methods",
    });
  };

  const closeRecoveryChooser = () => {
    resetRecoveryUi();
    const ss = readVerifySession() || {};
    writeVerifySession({
      ...ss,
      isRecoveryMode: false,
      recoveryUiStep: null,
      recoveryLostMethod: "",
      recoveryRemainingKeys: [],
      recoveryVerifiedKeys: [],
      recoveryResetAck: false,
      verifySubStep: "methods",
    });
  };

  const exitRecoveryToLoginFirstScreen = () => {
    if (recoveryRedirectTimerRef.current) {
      window.clearTimeout(recoveryRedirectTimerRef.current);
      recoveryRedirectTimerRef.current = null;
    }
    isRecoveryModeRef.current = false;
    resetRecoveryUi();
    setRecoverySelectLost("");
    setRecoveryRestrictionHrs(24);
    setLoginPendingVerification(false);
    setWizardStep(1);
    setLoginCredentialStep("identifier");
    setVerifySubStep("methods");
    challengeIdRef.current = "";
    tempTokenRef.current = "";
    clearVerifySession();
    navigate("/login", { replace: true });
  };

  const openRecoveryMethodVerify = async (methodKey) => {
    const key = normalizeRecoveryMethodKey(methodKey);
    const type = recoveryKeyToMethodType(key);
    if (!key || !type) return;
    setSelectedAuthMethod(type);
    setOtpSingle("");
    setRecoveryUiStep("verify");
    persistRecoverySession({
      recoveryUiStep: "verify",
      selectedAuthMethod: type,
    });
    if (key === "email" || key === "phone") {
      await sendRecoveryOtp(key);
    }
  };

  const resetLoginCaptcha = () => {
    setCaptchaToken(null);
    recaptchaRef.current?.reset();
  };

  // Handle login - Step 1: Validate credentials
  const handleLogin = async (inputSignId, loginPassword, token, loginContext = null) => {
    try {
      getOrCreateDeviceId();
      const result = await AuthService.login(inputSignId, loginPassword, token, { bindIp });
      if (result?.success) {
        setIdentifierDangerBorder(false);
        setPasswordDangerBorder(false);
        const responseData = result?.data;

        if (isLoginChallengePending(responseData)) {
          applyChallengeFromLoginData(responseData);
          const credKind = loginContext?.credentialKind === "phone" ? "phone" : "email_user";
          loginCredentialKindRef.current = credKind;

          loginSignIdRef.current = inputSignId;
          setLoginSignId(inputSignId);
          const phoneCc =
            credKind === "phone"
              ? String(loginContext?.countryCode || countryCode || "").trim()
              : "";
          loginVerifyPhoneCountryCodeRef.current = phoneCc;
          setLoginVerifyPhoneCountryCode(phoneCc);
          const methods = determineAvailableMethods(responseData);
          availableMethodsRef.current = methods;
          setAvailableMethods(methods);

          const hasPasskey =
            !!responseData?.hasPasskey || methods.some((m) => m.type === 4);

          if (hasPasskey && passkeySupported && !passkeyCancelledThisLoginRef.current) {
            setIsPasskeyLoading(true);
            const passkeyResult = await attemptSilentPasskeyLoginVerification(inputSignId);
            setIsPasskeyLoading(false);

            if (!passkeyResult.success && passkeyResult.reason === "cancelled") {
              passkeyCancelledThisLoginRef.current = true;
            }

            if (passkeyResult.success) {
              const verifyOnly = { success: true, data: passkeyResult.verifyData };
              // Passkey-only login: tokens from verify → done (no other methods)
              if (persistPasskeyLoginAndRedirect(verifyOnly)) {
                return;
              }
              if (passkeyResult.verifyData?.verification_complete === false) {
                responseData.remaining_methods = passkeyResult.verifyData.remaining_methods;
                responseData.completed_methods = passkeyResult.verifyData.completed_methods;
              } else if (passkeyResult.verifyData?.verified && passkeyResult.verifyData?.userId) {
                const loginResult = await AuthService.completePasskeyLogin(
                  inputSignId,
                  passkeyResult.verifyData,
                  challengeOptsFromRefs()
                );
                if (persistPasskeyLoginAndRedirect(loginResult)) {
                  return;
                }
                if (loginResult?.data?.verification_complete === false) {
                  responseData.remaining_methods = loginResult.data.remaining_methods;
                  responseData.completed_methods = loginResult.data.completed_methods;
                }
              }
            }
          }

          const preferSms = credKind === "phone" || accountTab === "phone";
          let selected = choose2FADefaultMethod(
            responseData,
            methods,
            passkeyCancelledThisLoginRef.current,
            preferSms
          );
          if (credKind === "phone") {
            const dm = responseData?.defaultMethod;
            const has2 = methods.some((m) => m.type === 2);
            const has4 = methods.some((m) => m.type === 4);
            if (dm === 2 && has2) selected = 2;
            else if (dm === 4 && has4 && passkeySupported) selected = 4;
            else selected = 3;
          }
          setSelectedAuthMethod(selected);

          setOtpSingle("");
          setResendTimer(0);
          setVerifySubStep("methods");
          setVerifyMethodBusyType(null);
          setVerificationSkipsPasswordSteps(false);
          setLoginPendingVerification(true);
          setWizardStep(2);

          const mode = String(responseData?.verification_mode || "");
          setVerificationMode(mode);
          const rem = remainingMethodTypes(responseData?.remaining_methods);
          const comp = remainingMethodTypes(responseData?.completed_methods);
          const recoverable = captureRecoveryHints(responseData);
          if (mode === "ALL_REQUIRED") {
            setRemainingMethods(rem.length ? rem : methods.map((m) => m.type).filter(Boolean));
            setCompletedMethods(comp);
          } else {
            setRemainingMethods(rem);
            setCompletedMethods(comp);
          }

          writeVerifySession({
            pending: true,
            verifySubStep: "methods",
            loginSignId: inputSignId,
            loginCredentialKind: credKind,
            loginOtpPhoneCountryCode: phoneCc || undefined,
            availableMethods: methods,
            selectedAuthMethod: selected,
            resendTimer: 0,
            verificationSkipsPasswordSteps: false,
            challenge_id: challengeIdRef.current,
            tempToken: tempTokenRef.current,
            verification_mode: mode,
            device_id: responseData?.device_id || undefined,
            bind_ip: !!responseData?.bind_ip,
            remaining_methods:
              mode === "ALL_REQUIRED"
                ? rem.length
                  ? rem
                  : methods.map((m) => m.type)
                : rem,
            completed_methods: comp,
            recovery_allowed: !!responseData?.recovery_allowed || recoverable.length > 0,
            recoverable_methods: recoverable,
            isRecoveryMode: false,
            recoveryLostMethod: "",
            recoveryRemainingKeys: [],
            recoveryVerifiedKeys: [],
          });
          // Do not send email/SMS OTP here — even when only one method is listed.
          // OTP is sent when the user taps Verify (handleStartMethodVerify).
          navigate("/auth/verify?type=1&action=login", { replace: true });
        } else {
          const didPersist = persistSessionAndRedirect(result, {
            successMessage: "Login success!",
            remember: rememberMe,
          });
          if (!didPersist) {
            alertErrorMessage(result?.message || "Login failed");
          }
        }

      } else {
        setPasswordDangerBorder(false);
        setIdentifierDangerBorder(false);
        const failMsg = result?.message || result?.data?.message;
        const failKind = classifyLoginFailureMessage(failMsg);
        if (failKind === "activation") {
          resetLoginCaptcha();
          const registrationToken = typeof result?.data === "string" ? result.data.trim() : "";
          if (registrationToken) {
            navigate("/signup", {
              state: {
                accountTab: accountTab === "phone" ? "phone" : "email",
                signId: inputSignId,
                countryCode: countryCode || "",
                password: loginPassword,
                registrationToken,
              },
            });
          } else {
            setIdentifierDangerBorder(true);
            alertErrorMessage(failMsg);
          }
          return;
        }
        if (failKind === "user_not_found") {
          setIdentifierDangerBorder(true);
        } else if (failKind === "wrong_password" || failKind === "auth_failed") {
          setPasswordDangerBorder(true);
        }
        alertErrorMessage(failMsg || "Login failed");
        resetLoginCaptcha();
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Network error. Please try again.";
      alertErrorMessage(msg);
      resetLoginCaptcha();
    }
  };

  const handleOpenOptionsPopup = () => {
    // If user has no other enabled methods besides passkey, don't show an empty picker.
    // Automatically fallback to Email OTP.
    if (!availableMethods.some((m) => m?.type && m.type !== 4)) {
      void startEmailOtpFallback({
        signIdForSession: loginSignIdRef.current || loginSignId,
        methodsForSession: availableMethods,
        verificationSkipsPasswordSteps,
      });
      return;
    }
    if (selectedAuthMethod === 4) {
      setSuppressPasskeyOverlay(true);
      window.setTimeout(() => {
        $("#VerificationOptionsModal").modal("show");
      }, 230);
    } else {
      $("#VerificationOptionsModal").modal("show");
    }
  };

  const handleSelectMethod = async (method) => {
    setSuppressPasskeyOverlay(false);
    if (method.type === 4) {
      passkeyCancelledThisLoginRef.current = false;
    }
    setSelectedAuthMethod(method.type);
    setOtpSingle("");
    setResendTimer(0);
    $("#VerificationOptionsModal").modal("hide");
    setVerifySubStep("code");
    const ss = readVerifySession() || {};
    writeVerifySession({
      ...ss,
      pending: true,
      verifySubStep: "code",
      selectedAuthMethod: method.type,
      challenge_id: challengeIdRef.current,
      tempToken: tempTokenRef.current,
    });

    if (method.type === 1) {
      await sendLoginOtp(1);
    } else if (method.type === 3) {
      await sendLoginOtp(3);
    }
  };

  const handleStartMethodVerify = async (method) => {
    const type = Number(method?.type);
    if (!type || completedMethods.includes(type) || verifyMethodBusyType != null) return;

    setSelectedAuthMethod(type);
    setOtpSingle("");
    setSuppressPasskeyOverlay(false);
    if (type === 4) {
      passkeyCancelledThisLoginRef.current = false;
    }

    setVerifyMethodBusyType(type);
    try {
      // Email / phone: send OTP only after Verify. Authenticator / passkey skip send-otp.
      if (methodNeedsOtpSend(type)) {
        const sent = await sendLoginOtp(type);
        if (!sent) return;
      }
      setVerifySubStep("code");
      const ss = readVerifySession() || {};
      writeVerifySession({
        ...ss,
        pending: true,
        verifySubStep: "code",
        selectedAuthMethod: type,
        challenge_id: challengeIdRef.current,
        tempToken: tempTokenRef.current,
        completed_methods: completedMethods,
        remaining_methods: remainingMethods,
      });
    } finally {
      setVerifyMethodBusyType(null);
    }
  };

  const handleBackToMethodList = () => {
    setVerifySubStep("methods");
    setOtpSingle("");
    setSuppressPasskeyOverlay(false);
    setIsPasskeyLoading(false);
    if (isRecoveryMode || recoveryUiStep) {
      closeRecoveryChooser();
    }
    const ss = readVerifySession() || {};
    writeVerifySession({
      ...ss,
      pending: true,
      verifySubStep: "methods",
      challenge_id: challengeIdRef.current,
      tempToken: tempTokenRef.current,
      completed_methods: completedMethods,
      remaining_methods: remainingMethods,
      selectedAuthMethod,
      isRecoveryMode: false,
      recoveryLostMethod: "",
      recoveryRemainingKeys: [],
      recoveryVerifiedKeys: [],
    });
  };

  const handleSwitchCodeMethod = async (method) => {
    const type = Number(method?.type);
    if (!type) return;
    if (isRecoveryMode) {
      const key = methodTypeToRecoveryKey(type);
      if (recoveryVerifiedKeys.includes(key)) return;
      if (key && !recoveryRemainingKeys.includes(key) && !recoveryVerifiedKeys.includes(key)) return;
    } else if (completedMethods.includes(type) && verificationMode === "ALL_REQUIRED") {
      return;
    }
    setSelectedAuthMethod(type);
    setOtpSingle("");
    setSuppressPasskeyOverlay(false);
    if (type === 4) {
      passkeyCancelledThisLoginRef.current = false;
    }
    const ss = readVerifySession() || {};
    writeVerifySession({
      ...ss,
      pending: true,
      verifySubStep: "code",
      selectedAuthMethod: type,
      challenge_id: challengeIdRef.current,
      tempToken: tempTokenRef.current,
    });
    if (isRecoveryMode) {
      const key = methodTypeToRecoveryKey(type);
      if (key === "email" || key === "phone") {
        await sendRecoveryOtp(key);
      }
      return;
    }
    if (methodNeedsOtpSend(type)) {
      await sendLoginOtp(type);
    }
  };

  const handleCloseOptionsPopup = () => {
    $("#VerificationOptionsModal").modal("hide");
    setSuppressPasskeyOverlay(false);
  };

  /** Passkey Authentication overlay: close → back to method list */
  const handleClosePasskeyModal = () => {
    setIsPasskeyLoading(false);
    passkeyCancelledThisLoginRef.current = true;
    handleBackToMethodList();
  };

  const handleAuthVerify = async (codeOverride) => {
    const otpCode = String(codeOverride ?? otpSingle).replace(/\D/g, "").slice(0, 6);
    if (!otpCode || otpCode.length < 6) {
      alertErrorMessage("Please enter a valid 6-digit code");
      return;
    }

    setLoginOtpVerifyBusy(true);
    try {
      if (isRecoveryModeRef.current || recoveryUiStepRef.current === "verify") {
        await handleRecoveryVerify(otpCode);
        return;
      }

      const isPhoneCred = loginCredentialKindRef.current === "phone";
      const verifyType =
        isPhoneCred && selectedAuthMethod !== 2 && selectedAuthMethod !== 4 ? 3 : selectedAuthMethod;

      // Get the correct identifier based on selected method
      // For mobile verification (type 3), we need to find the mobile number from availableMethods
      // For email verification (type 1), use the email
      // For Google Auth (type 2), the loginSignId (email/phone used for login) works
      const effectiveLoginId = String(loginSignIdRef.current || loginSignId || "").trim();
      let verifySignId = effectiveLoginId;

      const methodsForVerify =
        availableMethodsRef.current && availableMethodsRef.current.length
          ? availableMethodsRef.current
          : availableMethods;

      if (verifyType === 3) {
        const mobileMethod = methodsForVerify.find(m => m.type === 3);
        const rawMobile = mobileMethod?.value != null ? mobileMethod.value : effectiveLoginId;
        verifySignId = formatPhoneForUserOtpPayload(rawMobile, effectiveLoginId);
      } else if (verifyType === 1) {
        // Find email method and get its actual value (not masked)
        const emailMethod = methodsForVerify.find(m => m.type === 1);
        if (emailMethod?.value) {
          verifySignId = emailMethod.value;
        }
      }

      const result = await AuthService.getCode(verifySignId, verifyType, otpCode, challengeOptsFromRefs());

      if (result?.code === "DEVICE_MISMATCH_CHALLENGE" || result?.code === "CHALLENGE_INVALID" || result?.code === "CHALLENGE_EXPIRED") {
        alertErrorMessage(result?.message || "Login challenge expired. Please sign in again.");
        clearVerifySession();
        navigate("/login", { replace: true });
        return;
      }

      if (result?.success) {
        if (result?.data?.verification_complete === false) {
          handleIntermediateVerifyProgress(result.data);
          return;
        }
        const ok = persistSessionAndRedirect(result, {
          successMessage: "Login success!",
          remember: rememberMe,
        });
        if (!ok) alertErrorMessage(result?.message || "Verification succeeded but login session could not be saved.");
      } else {
        alertErrorMessage(result?.message || "Verification failed");
      }
    } catch (error) {
      alertErrorMessage("Verification failed. Please try again.");
    } finally {
      setLoginOtpVerifyBusy(false);
    }
  };

  handleAuthVerifyRef.current = handleAuthVerify;

  useEffect(() => {
    const inRecoveryVerify = false;
    const inNormalCode = wizardStep === 2 && verifySubStep === "code" && !recoveryUiStep;
    if (!inRecoveryVerify && !inNormalCode) {
      loginAutoVerifyLastOtpRef.current = "";
      loginAutoVerifyInFlightRef.current = false;
      return;
    }
    if (!inRecoveryVerify && selectedAuthMethod === 4) return;
    if (loginOtpVerifyBusy || loginAutoVerifyInFlightRef.current) return;
    const otpCode = otpSingle.replace(/\D/g, "").slice(0, 6);
    if (otpCode.length < 6) {
      loginAutoVerifyLastOtpRef.current = "";
      return;
    }
    if (loginAutoVerifyLastOtpRef.current === otpCode) return;
    loginAutoVerifyLastOtpRef.current = otpCode;
    loginAutoVerifyInFlightRef.current = true;
    void Promise.resolve(handleAuthVerifyRef.current()).finally(() => {
      loginAutoVerifyInFlightRef.current = false;
    });
  }, [otpSingle, wizardStep, verifySubStep, selectedAuthMethod, loginOtpVerifyBusy, recoveryUiStep]);

  const switchLoginTab = (tab) => {
    if (wizardStep === 2) return;
    loginCredentialKindRef.current = "email_user";
    setAccountTab(tab);
    setSignId("");
    setPassword("");
    setShowPassword(false);
    setBindIp(false);
    setWizardStep(1);
    setLoginPendingVerification(false);
    setVerificationSkipsPasswordSteps(false);
    setOtpSingle("");
    availableMethodsRef.current = [];
    setAvailableMethods([]);
    setResendTimer(0);
    loginSignIdRef.current = "";
    setLoginSignId("");
    loginVerifyPhoneCountryCodeRef.current = "";
    setLoginVerifyPhoneCountryCode("");
    challengeIdRef.current = "";
    tempTokenRef.current = "";
    setVerificationMode("");
    setRemainingMethods([]);
    setCompletedMethods([]);
    setIdentifierDangerBorder(false);
    setPasswordDangerBorder(false);
    setLoginNextBusy(false);
    setRecoverableMethods([]);
    setIsRecoveryMode(false);
    isRecoveryModeRef.current = false;
    setRecoveryLostMethod("");
    setRecoveryRemainingKeys([]);
    setRecoveryVerifiedKeys([]);
    setRecoveryUiStep(null);
    setRecoverySelectLost("");
    setRecoveryResetAck(false);
    setRecoveryRestrictionHrs(24);
    passkeyCancelledThisLoginRef.current = false;
    clearVerifySession();
  };

  const validateEmailOrUsername = () => {
    const raw = signId.trim();
    if (!raw) {
      setIdentifierDangerBorder(true);
      alertErrorMessage("Please enter your email or username");
      return false;
    }
    if (raw.includes("@")) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(raw)) {
        setIdentifierDangerBorder(true);
        alertErrorMessage("Please enter a valid email address");
        return false;
      }
    } else if (raw.length < 3) {
      setIdentifierDangerBorder(true);
      alertErrorMessage("Username must be at least 3 characters");
      return false;
    } else if (!/^[a-zA-Z0-9._-]+$/.test(raw)) {
      setIdentifierDangerBorder(true);
      alertErrorMessage("Username contains invalid characters");
      return false;
    }
    setIdentifierDangerBorder(false);
    return true;
  };

  const submitLoginStep1 = async () => {
    if (accountTab === "qr") {
      alertErrorMessage("QR code login is coming soon.");
      return;
    }
    setPasswordDangerBorder(false);
    const passwordCheck = validatePasswordAgainstRules(password, loginPasswordRules);
    if (!passwordCheck.ok) {
      alertErrorMessage(passwordCheck.message);
      return;
    }
    if (!captchaToken) {
      alertErrorMessage("Please complete the CAPTCHA first!");
      return;
    }
    if (accountTab === "email_user") {
      if (!validateEmailOrUsername()) return;
      setLoginNextBusy(true);
      try {
        await handleLogin(signId.trim(), password, captchaToken, { credentialKind: "email_user" });
      } finally {
        setLoginNextBusy(false);
      }
    } else {
      const digits = String(signId || "").replace(/\D/g, "").replace(/^0+/, "") || "";
      if (!digits) {
        setIdentifierDangerBorder(true);
        alertErrorMessage("Please enter your phone number");
        return;
      }
      const fullPhone = `${countryCode}${digits}`;
      if (!isValidPhoneNumber(fullPhone)) {
        setIdentifierDangerBorder(true);
        alertErrorMessage("Please enter a valid phone number for the selected country");
        return;
      }
      setIdentifierDangerBorder(false);
      if (digits !== signId) setSignId(digits);
      setLoginNextBusy(true);
      try {
        await handleLogin(digits, password, captchaToken, {
          credentialKind: "phone",
          countryCode: String(countryCode || "").trim(),
        });
      } finally {
        setLoginNextBusy(false);
      }
    }
  };

  const getNormalizedLoginId = () => {
    if (accountTab === "email_user") {
      return signId.trim();
    }
    const digits = String(signId || "").replace(/\D/g, "").replace(/^0+/, "") || "";
    return digits;
  };

  const loginWithPasskeyIfAvailable = async (normalizedId) => {
    if (!passkeySupported || !normalizedId) return false;
    setIsPasskeyLoading(true);
    try {
      const passkeyResult = await attemptSilentPasskeyLoginVerification(normalizedId);
      if (!passkeyResult?.success) {
        if (passkeyResult?.reason === "cancelled") {
          passkeyCancelledThisLoginRef.current = true;
        }
        return false;
      }

      // verifyPasskeyAuth now issues tokens (passkey-only login)
      const verifyOnly = { success: true, data: passkeyResult.verifyData };
      if (persistPasskeyLoginAndRedirect(verifyOnly)) return true;

      // Legacy fallback if verify returned verified without tokens
      if (passkeyResult.verifyData?.verified && passkeyResult.verifyData?.userId) {
        const loginResult = await AuthService.completePasskeyLogin(
          normalizedId,
          passkeyResult.verifyData
        );
        if (persistPasskeyLoginAndRedirect(loginResult)) return true;
        alertErrorMessage(loginResult?.message || "Passkey login could not be completed. Try password or try again.");
      }
      return false;
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  const submitLoginIdentifierOnly = async () => {
    if (accountTab === "qr") {
      alertErrorMessage("QR code login is coming soon.");
      return;
    }

    if (accountTab === "email_user") {
      if (!validateEmailOrUsername()) return;
    } else {
      const digits = getNormalizedLoginId();
      if (!digits) {
        setIdentifierDangerBorder(true);
        alertErrorMessage("Please enter your phone number");
        return;
      }
      const fullPhone = `${countryCode}${digits}`;
      if (!isValidPhoneNumber(fullPhone)) {
        setIdentifierDangerBorder(true);
        alertErrorMessage("Please enter a valid phone number for the selected country");
        return;
      }
      setIdentifierDangerBorder(false);
      if (digits !== signId) setSignId(digits);
    }

    const normalizedId = getNormalizedLoginId();

    setLoginNextBusy(true);
    try {
      const identifierKind = accountTab === "phone" ? "phone" : "email";
      const loginCheck = await AuthService.checkIdentifier({
        identifier: normalizedId,
        kind: identifierKind,
        purpose: "login",
        countryCode: accountTab === "phone" ? countryCode : undefined,
      });
      const accountCheck = parseIdentifierCheckResponse(loginCheck, "login");
      if (!accountCheck.ok) {
        setIdentifierDangerBorder(true);
        alertErrorMessage(accountCheck.message || "User not found");
        return;
      }
      if (!accountCheck.exists) {
        setIdentifierDangerBorder(true);
        alertErrorMessage(accountCheck.message || "User not found");
        return;
      }
      setIdentifierDangerBorder(false);

      const didPasskeyLogin = await loginWithPasskeyIfAvailable(normalizedId);
      if (didPasskeyLogin) return;

      setPasswordDangerBorder(false);
      setLoginCredentialStep("password");
      requestAnimationFrame(() => {
        document.getElementById("login-wizard-password")?.focus();
      });
    } finally {
      setLoginNextBusy(false);
    }
  };

  // Note: "Back" from verification step UI is currently commented out in JSX.

  // Google login
  const loginWithGoogle = useGoogleLogin({
    onSuccess: tokenResponse => {
      if (tokenResponse.access_token) {
        handleLoginGoogle(tokenResponse);
      }
    }
  });

  const completeThirdPartyLogin = async (result) => {
      if (result?.success) {
        const responseData = result?.data;

        if (isLoginChallengePending(responseData)) {
          applyChallengeFromLoginData(responseData);
          const googleLoginSignId = responseData?.signId || "";
          loginSignIdRef.current = googleLoginSignId;
          setLoginSignId(googleLoginSignId);
          loginCredentialKindRef.current = "email_user";
          loginVerifyPhoneCountryCodeRef.current = "";
          setLoginVerifyPhoneCountryCode("");

          const methods = determineAvailableMethods(responseData);
          availableMethodsRef.current = methods;
          setAvailableMethods(methods);

          const hasPasskey =
            !!responseData?.hasPasskey || methods.some((m) => m.type === 4);

          if (hasPasskey && passkeySupported && googleLoginSignId && !passkeyCancelledThisLoginRef.current) {
            setIsPasskeyLoading(true);
            const passkeyResult = await attemptSilentPasskeyLoginVerification(googleLoginSignId);
            setIsPasskeyLoading(false);

            if (!passkeyResult.success && passkeyResult.reason === "cancelled") {
              passkeyCancelledThisLoginRef.current = true;
            }

            if (passkeyResult.success) {
              const verifyOnly = { success: true, data: passkeyResult.verifyData };
              if (persistPasskeyLoginAndRedirect(verifyOnly)) {
                return;
              }
              if (passkeyResult.verifyData?.verification_complete === false) {
                responseData.remaining_methods = passkeyResult.verifyData.remaining_methods;
                responseData.completed_methods = passkeyResult.verifyData.completed_methods;
              } else if (passkeyResult.verifyData?.verified && passkeyResult.verifyData?.userId) {
                const loginResult = await AuthService.completePasskeyLogin(
                  googleLoginSignId,
                  passkeyResult.verifyData,
                  challengeOptsFromRefs()
                );
                if (persistPasskeyLoginAndRedirect(loginResult)) {
                  return;
                }
                if (loginResult?.data?.verification_complete === false) {
                  responseData.remaining_methods = loginResult.data.remaining_methods;
                  responseData.completed_methods = loginResult.data.completed_methods;
                }
              }
            }
          }

          const selected = choose2FADefaultMethod(
            responseData,
            methods,
            passkeyCancelledThisLoginRef.current,
            false
          );
          setSelectedAuthMethod(selected);
          setOtpSingle("");
          setResendTimer(0);
          setVerifySubStep("methods");
          setVerifyMethodBusyType(null);
          setVerificationSkipsPasswordSteps(true);
          setLoginPendingVerification(true);
          setWizardStep(2);

          const mode = String(responseData?.verification_mode || "");
          setVerificationMode(mode);
          const rem = remainingMethodTypes(responseData?.remaining_methods);
          const comp = remainingMethodTypes(responseData?.completed_methods);
          const recoverable = captureRecoveryHints(responseData);
          if (mode === "ALL_REQUIRED") {
            setRemainingMethods(rem.length ? rem : methods.map((m) => m.type).filter(Boolean));
            setCompletedMethods(comp);
          } else {
            setRemainingMethods(rem);
            setCompletedMethods(comp);
          }

          writeVerifySession({
            pending: true,
            verifySubStep: "methods",
            loginSignId: googleLoginSignId,
            loginCredentialKind: "email_user",
            loginOtpPhoneCountryCode: undefined,
            availableMethods: methods,
            selectedAuthMethod: selected,
            resendTimer: 0,
            verificationSkipsPasswordSteps: true,
            challenge_id: challengeIdRef.current,
            tempToken: tempTokenRef.current,
            verification_mode: mode,
            device_id: responseData?.device_id || undefined,
            bind_ip: !!responseData?.bind_ip,
            remaining_methods:
              mode === "ALL_REQUIRED"
                ? rem.length
                  ? rem
                  : methods.map((m) => m.type)
                : rem,
            completed_methods: comp,
            recovery_allowed: !!responseData?.recovery_allowed || recoverable.length > 0,
            recoverable_methods: recoverable,
            isRecoveryMode: false,
            recoveryLostMethod: "",
            recoveryRemainingKeys: [],
            recoveryVerifiedKeys: [],
          });
          // Do not send email/SMS OTP here — wait for Verify on the method list.
          navigate("/auth/verify?type=1&action=login", { replace: true });
        } else if (responseData?.token || responseData?.access_token) {
          const ok = persistSessionAndRedirect(result, {
            successMessage: "Login success!",
            remember: rememberMe,
          });
          if (!ok) alertErrorMessage(result?.message || "Login failed");
        }
      } else {
        alertErrorMessage(result?.message);
      }
  };

  const handleLoginGoogle = async (tokenResponse) => {
    LoaderHelper.loaderStatus(true);
    try {
      getOrCreateDeviceId();
      const result = await AuthService.googleLogin(tokenResponse, "", { bindIp });
      await completeThirdPartyLogin(result);
    } catch (error) {
      alertErrorMessage(error?.message || "Login failed");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleLoginApple = async () => {
    if (!isAppleSignInConfigured()) {
      alertErrorMessage("Apple sign-in is not configured.");
      return;
    }
    LoaderHelper.loaderStatus(true);
    try {
      const signed = await signInWithApple();
      console.log("[Apple login] signed data", signed);
      if (!signed?.idToken) return;
      getOrCreateDeviceId();
      const result = await AuthService.appleLogin(signed, { bindIp });
      await completeThirdPartyLogin(result);
    } catch (error) {
      alertErrorMessage(error?.message || "Apple sign-in failed");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };


  // Handle Sign in with Passkey (discoverable credentials)
  const handlePasskeyLogin = async () => {
    if (!passkeySupported) {
      alertErrorMessage('Passkeys are not supported on this device/browser');
      return;
    }

    try {
      setIsPasskeyLoading(true);
      LoaderHelper.loaderStatus(true);

      // Step 1: Get authentication options from server
      const optionsResult = await AuthService.passkeyDiscoverableAuthOptions();
      if (!optionsResult?.success || !optionsResult?.data) {
        alertErrorMessage(optionsResult?.message || 'Failed to get authentication options');
        return;
      }

      const challenge = optionsResult.challenge; // Store challenge to send back

      // Step 2: Prompt user to select a passkey
      let credential;
      try {
        credential = await startAuthentication(optionsResult.data);
      } catch (webauthnError) {
        if (webauthnError.name === 'NotAllowedError') {
          alertErrorMessage('Authentication cancelled or no passkey available');
        } else if (webauthnError.name === 'AbortError') {
          alertErrorMessage('Authentication was cancelled');
        } else {
          alertErrorMessage('Failed to authenticate with passkey');
        }
        return;
      }

      // Step 3: Verify the credential with server and get login token
      const verifyResult = await AuthService.passkeyDiscoverableVerify(credential, challenge);

      if (verifyResult?.success) {
        if (persistPasskeyLoginAndRedirect(verifyResult)) {
          return;
        }
      }
      alertErrorMessage(verifyResult?.message || "Passkey verification failed");
    } catch (error) {
      alertErrorMessage(error?.message || 'Failed to sign in with passkey');
    } finally {
      setIsPasskeyLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  // Get verification title based on method (aligned with signup-style headings)
  const getVerificationTitle = () => {
    switch (selectedAuthMethod) {
      case 1:
        return "Verify Your Email";
      case 2:
        return "Verify with authenticator";
      case 3:
        return "Verify Your Phone";
      case 4:
        return "Passkey Authentication";
      default:
        return "Verification";
    }
  };

  // Get verification description
  const getVerificationDescription = () => {
    const selectedMethod = availableMethods.find((m) => m.type === selectedAuthMethod);
    const maskedValue = selectedMethod?.maskedValue;
    if (isRecoveryMode) {
      const lostLabel = recoveryLostMethodLabel(recoveryLostMethod);
      if (selectedAuthMethod === 1) {
        return `To disable ${lostLabel}, enter the 6-digit code sent to your email ${maskedValue || "—"}. Withdrawals will be paused for 24 hours after this.`;
      }
      if (selectedAuthMethod === 2) {
        return `To disable ${lostLabel}, enter the 6-digit code from your authenticator app. Withdrawals will be paused for 24 hours after this.`;
      }
      if (selectedAuthMethod === 3) {
        return `To disable ${lostLabel}, enter the 6-digit code sent to your phone ${maskedValue || "—"}. Withdrawals will be paused for 24 hours after this.`;
      }
      return `Prove this method to disable ${lostLabel}. Withdrawals will be paused for 24 hours after this.`;
    }
    const modeHint =
      verificationMode === "ALL_REQUIRED"
        ? " New device: complete all enabled security methods."
        : verificationMode === "ANY_ONE"
          ? " Trusted device: any one method is enough."
          : "";

    if (selectedAuthMethod === 1) {
      return `The verification code has been sent to your email ${maskedValue || "—"}, valid for 10 minutes.${modeHint}`;
    }
    if (selectedAuthMethod === 2) {
      return `Enter the 6-digit code from your authenticator app.${modeHint}`;
    }
    if (selectedAuthMethod === 3) {
      return `The verification code has been sent to your phone ${maskedValue || "—"}, valid for 10 minutes.${modeHint}`;
    }
    if (selectedAuthMethod === 4) {
      return `Click the button below to authenticate with your passkey.${modeHint}`;
    }
    return `Enter your verification code.${modeHint}`;
  };

  const getOtpDigitsStr = () => otpSingle.replace(/\D/g, "").slice(0, 6);

  const focusOtpIndex = (idx) => {
    requestAnimationFrame(() => {
      otpInputRefs.current[Math.min(Math.max(0, idx), 5)]?.focus();
    });
  };

  const handleOtpCellChange = (index, e) => {
    const raw = e.target.value.replace(/\D/g, "");
    const d = getOtpDigitsStr();
    const before = d.slice(0, index);
    const after = d.slice(index + 1);
    if (!raw) {
      setOtpSingle(before + after);
      return;
    }
    const merged = (before + raw + after).replace(/\D/g, "").slice(0, 6);
    setOtpSingle(merged);
    focusOtpIndex(Math.min(index + raw.length, 5));
  };

  const handleOtpCellKeyDown = (index, e) => {
    const d = getOtpDigitsStr();
    if (e.key === "Backspace") {
      if (d[index]) {
        e.preventDefault();
        setOtpSingle(d.slice(0, index) + d.slice(index + 1));
      } else if (index > 0) {
        e.preventDefault();
        setOtpSingle(d.slice(0, index - 1) + d.slice(index));
        focusOtpIndex(index - 1);
      }
    }
  };

  const handleOtpRowPaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    const raw = text.replace(/\D/g, "").slice(0, 6);
    if (!raw) return;
    e.preventDefault();
    setOtpSingle(raw);
    focusOtpIndex(Math.min(raw.length, 5));
  };

  const handleLoginOtpPaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const raw = text.replace(/\D/g, "").slice(0, 6);
      setOtpSingle(raw);
      focusOtpIndex(Math.min(raw.length, 5));
    } catch {
      alertErrorMessage("Unable to read clipboard.");
    }
  };

  const otpDigitsDisplay = getOtpDigitsStr();

  const recoveryPanelMethods = useMemo(() => {
    if (!isRecoveryMode) return availableMethods;
    const keys = [...new Set([...recoveryVerifiedKeys, ...recoveryRemainingKeys])];
    return keys.map((key) => recoveryMethodObject(key, availableMethods)).filter(Boolean);
  }, [isRecoveryMode, recoveryVerifiedKeys, recoveryRemainingKeys, availableMethods]);

  const recoveryCompletedTypes = useMemo(
    () => recoveryVerifiedKeys.map(recoveryKeyToMethodType).filter(Boolean),
    [recoveryVerifiedKeys]
  );
  const recoveryRemainingTypes = useMemo(
    () => recoveryRemainingKeys.map(recoveryKeyToMethodType).filter(Boolean),
    [recoveryRemainingKeys]
  );

  const showUnableToVerify =
    recoverableMethods.length > 0 && !isRecoveryMode && !recoveryUiStep;

  return (
    <>
      <Helmet>
        <title>Arab Global Crypto Exchange User Login – Trade Crypto Instantly</title>
        <meta
          name="description"
          content="Sign in to your Arab Global Crypto Exchange account and start trading Bitcoin, Ethereum and altcoins in minutes."
        />
        <meta
          name="keywords"
          content="login crypto exchange, Arab Global Crypto Exchange sign in, crypto trading portal, secure account Arab Global Crypto Exchange"
        />
      </Helmet>

      <div className="login_fullhieght login-page-center">
        <div className="login_section">
          <div className="login_form_right">
            <div className="form_block_login login-wizard-root">
              {wizardStep === 1 && (
                <>
                  <h1 className="login-wizard-title">Log In</h1>

                  <ul className="login-wizard-tabs" role="tablist">
                    <li>
                      <button
                        type="button"
                        className={accountTab === "email_user" ? "is-active" : ""}
                        role="tab"
                        aria-selected={accountTab === "email_user"}
                        onClick={() => switchLoginTab("email_user")}
                      >
                        Email/Username
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className={accountTab === "phone" ? "is-active" : ""}
                        role="tab"
                        aria-selected={accountTab === "phone"}
                        onClick={() => switchLoginTab("phone")}
                      >
                        Phone
                      </button>
                    </li>
                    {/* <li>
                      <button
                        type="button"
                        className={accountTab === "qr" ? "is-active" : ""}
                        role="tab"
                        aria-selected={accountTab === "qr"}
                        onClick={() => switchLoginTab("qr")}
                      >
                        QR Code
                      </button>
                    </li> */}
                  </ul>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (accountTab === "qr") return;
                      if (loginNextBusy) return;
                      if (loginCredentialStep === "identifier") void submitLoginIdentifierOnly();
                      else void submitLoginStep1();
                    }}
                    noValidate
                  >
                    <div className="row">
                      {accountTab === "email_user" && (
                        <div className="col-sm-12 input_block">
                          <EmailInputWithDomainSuggest
                            value={signId}
                            onChange={(e) => {
                              if (identifierDangerBorder) setIdentifierDangerBorder(false);
                              if (loginCredentialStep === "password") setLoginCredentialStep("identifier");
                              setSignId(e.target.value);
                            }}
                            wrapClassName={`email_code eids-email-wrap${identifierDangerBorder ? " signup-step1-email--error" : ""}`}
                            placeholder="Email/Username"
                            autoComplete="username"
                            inputMode="text"
                          />
                        </div>
                      )}

                      {accountTab === "phone" && (
                        <div className={`col-sm-12 input_block${identifierDangerBorder ? " login-phone-wrap--error" : ""}`}>
                          <AuthPhoneField
                            inputId="loginCountryCode"
                            countryCode={countryCode}
                            onCountryChange={(code) => {
                              if (identifierDangerBorder) setIdentifierDangerBorder(false);
                              setCountryCode(code);
                            }}
                            phone={signId}
                            onPhoneChange={(v) => {
                              if (identifierDangerBorder) setIdentifierDangerBorder(false);
                              setSignId(v);
                            }}
                            phonePlaceholder="Enter phone number"
                            selectProps={{
                              filterOption: (option, inputValue) =>
                                option.label.toLowerCase().includes(inputValue.toLowerCase()),
                            }}
                          />
                        </div>
                      )}

                      {accountTab === "qr" && (
                        <div className="col-sm-12 input_block qrcode_scan_block">

                          <div className="qrcode_scan">
                            <img src="/images/qrcode_img.svg" alt="QR Code" />
                          </div>
                          <sub>Log in with QR code</sub>

                          <p className="signup-wizard-subtitle">Scan this code with your <span>AGCXApp</span></p>

                          {/* <label><input type="checkbox" checked={bindIp} onChange={() => setBindIp((v) => !v)} /> Bind IP(Security option)</label> */}

                        </div>
                      )}

                      {accountTab !== "qr" && (
                        <>
                          {loginCredentialStep === "password" ? (
                            <div className="col-sm-12 input_block">
                              <div
                                className={`email_code${passwordDangerBorder ? " login-wizard-password--error" : ""}`}
                              >
                                <input
                                  id="login-wizard-password"
                                  className="input_filed"
                                  type={showPassword ? "text" : "password"}
                                  placeholder="Password"
                                  value={password}
                                  onChange={(e) => {
                                    setPassword(e.target.value);
                                    if (passwordDangerBorder) setPasswordDangerBorder(false);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key !== "Enter" || e.repeat) return;
                                    e.preventDefault();
                                    if (loginNextBusy || accountTab === "qr") return;
                                    void submitLoginStep1();
                                  }}
                                  autoComplete="current-password"
                                  style={{ paddingRight: 44 }}
                                />
                                <div
                                  className="get_otp otp2"
                                  role="button"
                                  tabIndex={0}
                                  onClick={handleTogglePassword}
                                  onKeyDown={(e) => e.key === "Enter" && handleTogglePassword()}
                                >
                                  {showPassword ? <i className="ri-eye-line" /> : <i className="ri-eye-off-line" />}
                                </div>
                              </div>
                            </div>
                          ) : null}

                          {loginCredentialStep === "password" && CAPTCHA_SITE_KEY ? (
                            <div className="col-sm-12 input_block login-wizard-captcha">
                              <ReCAPTCHA
                                key={captchaTheme}
                                ref={recaptchaRef}
                                sitekey={CAPTCHA_SITE_KEY}
                                theme={captchaTheme}
                                onChange={(token) => setCaptchaToken(token)}
                                onExpired={() => setCaptchaToken(null)}
                              />
                            </div>
                          ) : null}
                        </>
                      )}

                      {accountTab !== "qr" && (
                        <div className="col-sm-12">
                          <div className="login-wizard-bind-row forgot_password_link">
                            <label>
                              <input
                                type="checkbox"
                                checked={bindIp}
                                onChange={() => setBindIp((v) => !v)}
                              />{" "}
                              Bind IP (lock session to this network)
                            </label>
                            <Link to="/forgot_password">Forgot password</Link>
                          </div>
                        </div>
                      )}

                      {accountTab !== "qr" && (
                        <>
                          <div className="col-sm-12 login_btn">
                            <button
                              type="submit"
                              className="login-wizard-next-btn"
                              disabled={loginNextBusy}
                              aria-busy={loginNextBusy}
                            >
                              {loginNextBusy ? (
                                <>
                                  <span className="login-wizard-next-btn__spinner" aria-hidden />
                                  <span>
                                    {loginCredentialStep === "password" ? "Signing in…" : "Please wait…"}
                                  </span>
                                </>
                              ) : (
                                "Next"
                              )}
                            </button>
                          </div>

                          <div className="col-sm-12">
                            <div className="signup-wizard-divider">Or log in with</div>
                            {passkeySupported && (
                              <button
                                type="button"
                                className="login-wizard-passkey-outline"
                                onClick={handlePasskeyLogin}
                                disabled={isPasskeyLoading}
                              >
                                <img src="/images/keyicon.svg" alt="passkey" />
                                {isPasskeyLoading ? "Authenticating..." : "Log In with Passkey"}
                              </button>
                            )}
                            <div className="signup-wizard-social-row" style={{ marginTop: 14 }}>
                              <button type="button" className="signup-wizard-social-btn" onClick={() => loginWithGoogle()} aria-label="Google">
                                <img src="/images/google_icon.svg" alt="" />
                              </button>
                              <button
                                type="button"
                                className="signup-wizard-social-btn"
                                onClick={() => void handleLoginApple()}
                                aria-label="Apple"
                              >
                                <img src="/images/appleicon2.svg" alt="" />
                              </button>
                            </div>
                          </div>

                          <div className="col-sm-12">
                            <Link className="login-wizard-create-link" to="/signup">
                              Create a AGCX Account
                            </Link>
                          </div>
                        </>
                      )}
                    </div>
                  </form>
                </>
              )}

              {wizardStep === 2 && verifySubStep === "methods" && (
                <LoginMethodSelectPanel
                  methods={availableMethods}
                  completedTypes={completedMethods}
                  verificationMode={verificationMode}
                  busyType={verifyMethodBusyType}
                  onVerifyMethod={handleStartMethodVerify}
                  recoverableMethods={recoverableMethods}
                  recoveryBusy={recoveryStartBusy}
                  onUnableToVerify={openRecoveryChooser}
                />
              )}

              {wizardStep === 2 && verifySubStep === "code" && (
                <LoginMethodCodePanel
                  methods={isRecoveryMode ? recoveryPanelMethods : availableMethods}
                  selectedType={selectedAuthMethod}
                  completedTypes={isRecoveryMode ? recoveryCompletedTypes : completedMethods}
                  remainingTypes={isRecoveryMode ? recoveryRemainingTypes : remainingMethods}
                  verificationMode={isRecoveryMode ? "ALL_REQUIRED" : verificationMode}
                  title={getVerificationTitle()}
                  description={getVerificationDescription()}
                  otpDigitsDisplay={[0, 1, 2, 3, 4, 5].map((i) => otpDigitsDisplay[i] || "")}
                  otpInputRefs={otpInputRefs}
                  resendTimer={resendTimer}
                  verifyBusy={loginOtpVerifyBusy}
                  onSubmit={() => {
                    if (selectedAuthMethod === 4) return;
                    void handleAuthVerify();
                  }}
                  onResend={() => {
                    if (isRecoveryMode) {
                      const key = methodTypeToRecoveryKey(selectedAuthMethod);
                      if (key === "email" || key === "phone") void sendRecoveryOtp(key);
                      return;
                    }
                    if (selectedAuthMethod === 1 || selectedAuthMethod === 3) {
                      void sendLoginOtp(selectedAuthMethod);
                    }
                  }}
                  onPaste={handleLoginOtpPaste}
                  onCellChange={handleOtpCellChange}
                  onCellKeyDown={handleOtpCellKeyDown}
                  onRowPaste={handleOtpRowPaste}
                  onBackToMethods={handleBackToMethodList}
                  onSwitchMethod={handleSwitchCodeMethod}
                  showPasskeyPanel={selectedAuthMethod === 4 && !suppressPasskeyOverlay}
                  passkeyBusy={isPasskeyLoading}
                  onPasskeyAuth={handlePasskeyAuth}
                  onClosePasskey={handleClosePasskeyModal}
                  isRecovery={isRecoveryMode}
                  recoveryBusy={recoveryStartBusy}
                  onUnableToVerify={showUnableToVerify ? openRecoveryChooser : undefined}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <LoginMethodRecoveryOverlays
        step={recoveryUiStep}
        recoverableMethods={recoverableMethods}
        selectedLost={recoverySelectLost}
        availableMethods={availableMethods}
        remainingKeys={recoveryRemainingKeys}
        verifiedKeys={recoveryVerifiedKeys}
        verifyType={selectedAuthMethod}
        otpValue={otpSingle}
        resendTimer={resendTimer}
        verifyBusy={loginOtpVerifyBusy}
        startBusy={recoveryStartBusy}
        resetAck={recoveryResetAck}
        restrictionHours={recoveryRestrictionHrs}
        onSelectLost={setRecoverySelectLost}
        onResetAck={setRecoveryResetAck}
        onClose={
          recoveryUiStep === "verify"
            ? () => setRecoveryUiStep(recoveryRemainingKeys.length || recoveryVerifiedKeys.length ? "requirements" : "select")
            : recoveryUiStep === "requirements"
              ? closeRecoveryChooser
              : recoveryUiStep === "success"
                ? exitRecoveryToLoginFirstScreen
                : closeRecoveryChooser
        }
        onSelectConfirm={() => {
          if (!recoverySelectLost) return;
          setRecoveryUiStep("warn");
          persistRecoverySession({
            recoveryUiStep: "warn",
            recoverySelectLost,
            recoveryResetAck,
          });
        }}
        onWarnCancel={() => setRecoveryUiStep("select")}
        onWarnConfirm={() => void handleStartMethodRecovery(recoverySelectLost)}
        onPickRemaining={(key) => void openRecoveryMethodVerify(key)}
        onRequirementsBack={() => setRecoveryUiStep("warn")}
        onVerifySubmit={(code) => void handleAuthVerify(code)}
        onGetCode={() => {
          const key = methodTypeToRecoveryKey(selectedAuthMethod) || recoveryRemainingKeys[0];
          if (key === "email" || key === "phone") void sendRecoveryOtp(key);
        }}
        onPaste={handleLoginOtpPaste}
        onOtpChange={setOtpSingle}
        onSuccessDone={exitRecoveryToLoginFirstScreen}
      />

      {/* Verification Options Modal - Same structure as SettingsPage */}
      <div className="modal fade search_form" id="VerificationOptionsModal" tabIndex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true" data-bs-backdrop="static">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">Choose Verification Method</h5>
              <p>Select how you’d like to confirm your identity</p>
              <button type="button" className="btn-close" onClick={handleCloseOptionsPopup} aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <form className="profile_form" onSubmit={(e) => e.preventDefault()}>

                {availableMethods.map((method) => {
                  const done = completedMethods.includes(method.type);
                  return (
                  <div className="" key={method.type}>
                    <div
                      className={`d-flex align-items-center justify-content-between text-white${done ? " opacity-50" : ""}`}
                      onClick={() => {
                        if (done && verificationMode === "ALL_REQUIRED") return;
                        handleSelectMethod(method);
                      }}
                      role="button"
                    >
                      <div className="d-flex align-items-start">
                        <i className={`${method.icon} me-2`}></i>
                        <div>
                          <strong>{method.label}{done ? " ✓" : ""}</strong>
                          <p className="mb-0  small">{done ? "Already verified for this login" : method.description}</p>
                        </div>
                      </div>
                      <i className="ri-arrow-right-s-line"></i>
                    </div>
                  </div>
                  );
                })}

              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
